import React, {FC, memo, useEffect, useState} from "react";
import {Button, Space} from "antd";
import {DownloadOutlined, PrinterOutlined, CloseOutlined} from "@ant-design/icons";
import DraggableModal from "../../../../views/DraggableModal/DraggableModal";
import {generateLetterPdf, LetterPdfData} from "./generateLetterPdf";
import i18n from "../../../../services/i18n";

/** A4 Hochformat-Seitenverhaeltnis (Breite / Hoehe = 210 / 297) */
const A4_ASPECT_RATIO = 210 / 297;
const PREVIEW_WIDTH = 800;
const PREVIEW_HEIGHT = PREVIEW_WIDTH / A4_ASPECT_RATIO;
const MODAL_WIDTH = PREVIEW_WIDTH + 50;

interface LetterPdfPreviewModalProps {
    open: boolean;
    onClose: () => void;
    letterData: LetterPdfData | null;
    fileName?: string;
}

/**
 * Vorschau-Modal fuer DIN-5008-Brief-PDFs.
 * Zeigt eine eingebettete PDF-Vorschau in einem verschiebbaren Modal
 * mit Buttons zum Herunterladen, Drucken und Schliessen.
 */
const LetterPdfPreviewModal: FC<LetterPdfPreviewModalProps> = ({open, onClose, letterData, fileName}) => {
    const [pdfDataUri, setPdfDataUri] = useState<string>("");

    // PDF-Vorschau generieren wenn Modal sichtbar wird oder sich die Daten aendern
    useEffect(() => {
        if (open && letterData) {
            let cancelled = false;
            generateLetterPdf(letterData, "preview").then((dataUri) => {
                if (!cancelled && typeof dataUri === "string") {
                    setPdfDataUri(dataUri);
                }
            });
            return () => {
                cancelled = true;
            };
        } else {
            setPdfDataUri("");
        }
    }, [open, letterData]);

    const handleExport = () => {
        if (!letterData) return;
        generateLetterPdf(letterData, "export", fileName);
    };

    const handlePrint = () => {
        if (!letterData) return;
        generateLetterPdf(letterData, "print");
    };

    return (
        <DraggableModal
            draggable={true}
            boundsPadding={500}
            open={open}
            onCancel={onClose}
            destroyOnClose
            width={MODAL_WIDTH}
            style={{top: 30}}
            title={
                <div>
                    {i18n.containers.courses.AttendanceList.exportPreview()}{" "}
                    {fileName || "Brief"}
                </div>
            }
            footer={
                <div style={{display: "flex", justifyContent: "flex-end"}}>
                    <Space>
                        <Button
                            icon={<DownloadOutlined />}
                            type="primary"
                            onClick={handleExport}
                        >
                            {i18n.containers.courses.AttendanceList.downloadPdf()}
                        </Button>
                        <Button
                            icon={<PrinterOutlined />}
                            onClick={handlePrint}
                        >
                            {i18n.containers.courses.AttendanceList.printPdf()}
                        </Button>
                        <Button
                            icon={<CloseOutlined />}
                            onClick={onClose}
                        >
                            {i18n.containers.courses.AttendanceList.closePreview()}
                        </Button>
                    </Space>
                </div>
            }
        >
            <div>
                {pdfDataUri ? (
                    <embed
                        width={PREVIEW_WIDTH}
                        height={PREVIEW_HEIGHT}
                        src={`${pdfDataUri}#toolbar=0&view=FitH&scrollbar=0`}
                    />
                ) : null}
            </div>
        </DraggableModal>
    );
};

export default memo(LetterPdfPreviewModal);
